import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Divider, IconButton, Link, Paper, Stack, Switch, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import IntensityRatioService from '../../../shared/services/intensity-ratio/intensity-ratio.service';
import { IIntensityRatio } from '../../../shared/services/intensity-ratio/intensity-ratio.model';

const intensityRatioService = new IntensityRatioService();

export const SettingsIntensityRatiosPage = () => {
  const [ratios, setRatios] = useState<IIntensityRatio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    intensityRatioService.getAll()
      .then(data => setRatios(data))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const toggleEnabled = (key: string) => {
    setRatios(prev => prev.map(r => (r.key === key ? { ...r, enabled: !r.enabled } : r)));
    setIsDirty(true);
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= ratios.length) return;
    setRatios(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    setIsSaving(true);
    const payload = ratios.map((r, index) => ({
      ratioKey: r.key,
      enabled: r.enabled,
      displayOrder: index + 1,
    }));
    intensityRatioService.update(payload)
      .then(data => {
        setRatios(data);
        setIsDirty(false);
      })
      .catch(console.error)
      .finally(() => setIsSaving(false));
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title="Intensity Ratios"
        includeBackButton={true}
        backButtonLink="/settings/company-settings/overview"
        backButtonText="Back to settings"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Intensity ratios express emissions relative to a business metric (revenue or number of
        employees). Choose which ratios apply to your organisation and the order they appear in.
        These settings drive the SECR report and the intensity ratios shown across the application.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Where do the revenue and employee figures come from?</AlertTitle>
        Intensity ratios are calculated using the <strong>total revenue</strong> and
        {' '}<strong>number of employees (UK / Non-UK)</strong> recorded for each reporting period.
        Set or update those figures on a reporting period's detail page.
        <Box sx={{ mt: 1 }}>
          <Link component={RouterLink} to="/settings/company-settings/reporting-periods" sx={{ fontWeight: 600 }}>
            Manage reporting periods →
          </Link>
        </Box>
      </Alert>

      <LoadingPage pageReady={!isLoading}>
        <Paper variant="outlined">
          {ratios.map((ratio, index) => (
            <Box key={ratio.key}>
              {index > 0 && <Divider />}
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ px: 2, py: 1.5 }}
              >
                <Switch
                  checked={ratio.enabled}
                  onChange={() => toggleEnabled(ratio.key)}
                  inputProps={{ 'aria-label': `Enable ${ratio.label}` }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{ratio.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{ratio.unitLabel}</Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    aria-label="Move up"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Move down"
                    disabled={index === ratios.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Paper>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      </LoadingPage>
    </PageOuterContainer>
  );
};
